import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';

const chainTypes = ['TRC20', 'ERC20', 'BEP20'];
const chainLabels: Record<string, string> = {
  TRC20: 'TRC20（推荐）',
  ERC20: 'ERC20',
  BEP20: 'BEP20',
};

function nowText() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function UsdtWithdrawPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [selectedChain, setSelectedChain] = useState('TRC20');
  const [walletAddress, setWalletAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [availableBalance, setAvailableBalance] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const inputAmount = parseFloat(amount) || 0;

  const loadBalance = useCallback(async () => {
    setLoaded(false);
    setLoadFailed(false);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoadFailed(true);
        return;
      }
      const { data } = await supabase
        .from('account_summary')
        .select('available_margin')
        .eq('user_id', user.id);
      const total = (data || []).reduce((sum, row) => sum + Number(row.available_margin || 0), 0);
      setAvailableBalance(total);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setAmount(val);
    }
  };

  const canSubmit = () => {
    return (
      inputAmount > 0 &&
      inputAmount <= availableBalance &&
      walletAddress.trim().length >= 10
    );
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('登录已失效');

      // 扣除可用资金
      const { data: summary } = await supabase
        .from('account_summary')
        .select('available_margin')
        .eq('user_id', user.id)
        .maybeSingle();
      const currentAvailable = Number(summary?.available_margin) || 0;
      if (currentAvailable < inputAmount) {
        throw new Error('可用资金不足，请刷新后重试');
      }
      const { error: updateErr } = await supabase
        .from('account_summary')
        .update({ available_margin: currentAvailable - inputAmount })
        .eq('user_id', user.id);
      if (updateErr) throw updateErr;

      // 写入出金记录
      const remark = JSON.stringify({
        chainType: selectedChain,
        walletAddress: walletAddress.trim(),
      });

      const { error } = await supabase.from('withdraw_records').insert({
        user_id: user.id,
        amount: inputAmount,
        method: 'USDT出金',
        status: 'pending',
        remark,
        create_time: nowText(),
      });
      if (error) throw error;

      // 通知管理员（不阻塞提交）
      supabase.functions.invoke('send-withdraw-email', {
        body: {
          userId: user.id,
          type: 'USDT',
          amount: inputAmount,
          chainType: selectedChain,
          walletAddress: walletAddress.trim(),
          createTime: nowText(),
        },
      }).catch(() => {});

      setSubmitted(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '提交失败', 'error');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brown-50 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-brown-900 text-lg font-bold mb-2">出金申请已提交</h2>
        <p className="text-brown-500 text-sm text-center">
          ¥{inputAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} (USDT {selectedChain})
        </p>
        <p className="text-brown-400 text-xs mt-2">后台审核通过后将为您转账</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setAmount('');
            setWalletAddress('');
          }}
          className="mt-6 px-6 py-2.5 rounded-full bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          继续出金
        </button>
        <button
          onClick={() => navigate('/profile/transactions')}
          className="mt-3 text-gold-500 text-sm cursor-pointer"
        >
          查看出金记录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">USDT 出金</span>
        <div className="w-8" />
      </div>

      {/* USDT标志 */}
      <div className="flex flex-col items-center pt-6 pb-4">
        <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-2">
          <span className="text-white text-2xl font-bold tracking-tight">T</span>
        </div>
        <h2 className="text-brown-900 text-lg font-bold">USDT 出金</h2>
        <p className="text-brown-500 text-xs mt-1">推荐使用 · 安全便捷</p>
      </div>

      {loadFailed ? (
        <div className="mx-4 mt-4 bg-white rounded-xl p-8 text-center">
          <i className="ri-error-warning-line text-brown-300 text-4xl"></i>
          <p className="text-brown-500 text-sm mt-3">数据加载失败，请检查登录状态</p>
          <button
            onClick={loadBalance}
            className="mt-4 px-5 py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            重新加载
          </button>
        </div>
      ) : !loaded ? (
        <div className="flex items-center justify-center py-24">
          <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
        </div>
      ) : (
        <div className="px-4 space-y-4">
          {/* 可出金额 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-brown-500 text-xs mb-1">可出金额</p>
            <p className="text-brown-900 text-2xl font-bold">
              ¥{availableBalance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
          </div>

          {/* 链类型选择 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <label className="block text-brown-600 text-sm mb-3">选择公链</label>
            <div className="flex gap-2 flex-wrap">
              {chainTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedChain(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    selectedChain === type
                      ? 'bg-gold-500 text-brown-950 border border-gold-500'
                      : 'bg-brown-50 text-brown-500 border border-brown-200'
                  }`}
                >
                  {chainLabels[type]}
                </button>
              ))}
            </div>
          </div>

          {/* 您的 USDT 钱包地址 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <label className="block text-brown-600 text-sm mb-2">您的 USDT 钱包地址</label>
            <input
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder="请填写您的 USDT 钱包地址"
              className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
            />
            <p className="text-brown-400 text-xs mt-2">请确保地址正确，审核通过后资金将转入该地址</p>
          </div>

          {/* 出金金额 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <label className="block text-brown-600 text-sm mb-2">出金金额</label>
            <div className="flex items-center gap-2 border-b-2 border-gold-200 pb-2">
              <span className="text-brown-900 text-xl font-bold">¥</span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                placeholder="输入金额"
                className="flex-1 text-brown-900 text-xl font-bold outline-none bg-transparent"
              />
            </div>
            <p className="text-brown-400 text-xs mt-2">
              可出 ¥{availableBalance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
          </div>

          {/* 提示 */}
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
            <div className="flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 flex-shrink-0">
                <i className="ri-information-line text-sm"></i>
              </span>
              <div>
                <p className="text-brown-600 text-xs">
                  请仔细核对钱包地址和公链类型，确认无误后再提交申请。
                </p>
                <p className="text-brown-500 text-xs mt-1">
                  后台审核通过后，资金将在 10~30 分钟内到账。
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 提交按钮 */}
      <div className="px-4 mt-6 mb-8">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit() || submitting || !loaded}
          className={`w-full py-3.5 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all ${
            canSubmit() && !submitting && loaded
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98] shadow-sm shadow-amber-500/20'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              提交中...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-upload-line"></i>
              确认出金
            </span>
          )}
        </button>
        <p className="text-brown-400 text-xs text-center mt-3">
          出金申请提交后，后台管理员审核通过将自动转账
        </p>
      </div>
    </div>
  );
}